import { Maybe } from '@crocodille/util';
import { useEffect } from 'react';

import {
  POSTCODE_VALIDATOR,
  REQUIRED_VALIDATOR_V2,
  useForm,
  useFormField,
  v2_useMaxLengthValidator,
} from '../modules/forms';

export interface AccountBillingAddressFormData {
  readonly company: string;
  readonly crn: string;
  readonly vat: string;
  readonly street: string;
  readonly number: string;
  readonly city: string;
  readonly postcode: string;
}

export const INITIAL_BILLING_ADDRESS_FORM_DATA: AccountBillingAddressFormData =
  {
    company: '',
    crn: '',
    vat: '',
    street: '',
    number: '',
    city: '',
    postcode: '',
  };

export function useAccountBillingAddressForm(
  onSubmit: (data: AccountBillingAddressFormData) => void,
  value: Maybe<AccountBillingAddressFormData>,
) {
  const company = useFormField({
    value: value?.company ?? INITIAL_BILLING_ADDRESS_FORM_DATA.company,
  });

  const crnMaxLengthValidator = v2_useMaxLengthValidator(8);

  const crn = useFormField({
    value: value?.crn ?? INITIAL_BILLING_ADDRESS_FORM_DATA.crn,
    validators: [crnMaxLengthValidator],
  });

  const vatMaxLengthValidator = v2_useMaxLengthValidator(14);

  const vat = useFormField({
    value: value?.vat ?? INITIAL_BILLING_ADDRESS_FORM_DATA.vat,
    validators: [vatMaxLengthValidator],
  });

  const street = useFormField({
    value: value?.street ?? INITIAL_BILLING_ADDRESS_FORM_DATA.street,
    required: true,
    validators: [REQUIRED_VALIDATOR_V2],
  });

  const number = useFormField({
    value: value?.number ?? INITIAL_BILLING_ADDRESS_FORM_DATA.number,
    required: true,
    validators: [REQUIRED_VALIDATOR_V2],
  });

  const city = useFormField({
    value: value?.city ?? INITIAL_BILLING_ADDRESS_FORM_DATA.city,
    required: true,
    validators: [REQUIRED_VALIDATOR_V2],
  });

  const postcode = useFormField({
    value: value?.postcode ?? INITIAL_BILLING_ADDRESS_FORM_DATA.postcode,
    required: true,
    validators: [REQUIRED_VALIDATOR_V2, POSTCODE_VALIDATOR],
  });

  const form = useForm({
    fields: {
      company,
      crn,
      vat,
      street,
      number,
      city,
      postcode,
    },
    onSubmit,
  });

  useEffect(() => {
    if (value) {
      form.reset(value);
    }
  }, [value]);

  return form;
}
